import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useState,
} from 'react';
import { AlertProps, SnackbarProps as MuiSnackbarProps } from '@mui/material';
import Snackbar, {
  SnackbarProps,
} from '@/components/@IntranetLibrary/Snackbar/index';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

type SnackbarOptions = {
  severity: AlertProps['severity'];
  duration?: number | null;
  position?: MuiSnackbarProps['anchorOrigin'];
  onClose?: () => void;
} & Pick<SnackbarProps, 'message' | 'link' | 'action'>;

export type SnackbarContextProps = {
  show: (options: SnackbarOptions) => void;
  showError: (options: Omit<SnackbarOptions, 'severity'>) => void;
  showErrorMessage: (
    e: unknown,
    options?: Omit<SnackbarOptions, 'severity' | 'message'>,
  ) => void;
  showSuccess: (options: Omit<SnackbarOptions, 'severity'>) => void;
};
const SnackbarContext = createContext<SnackbarContextProps | null>(null);
SnackbarContext.displayName = 'Snackbar';

/** 토스트 기본 노출 시간(ms). 토스트가 보일 때까지 화면 전환을 늦춰야 하는 쪽에서도 참조 */
export const SNACKBAR_DEFAULT_DURATION = 2000;

const SnackbarProvider = ({ children }: PropsWithChildren) => {
  const [toastProps, setToastProps] =
    useState<Pick<SnackbarProps, 'message' | 'link' | 'action'>>();
  const [snackbarProps, setSnackbarProps] = useState<MuiSnackbarProps>();
  const [iconProps, setIconProps] = useState<AlertProps>();
  const { t } = useTranslation();

  const show = useCallback(
    ({
      severity,
      duration = SNACKBAR_DEFAULT_DURATION,
      position = {
        vertical: 'top',
        horizontal: 'right',
      },
      onClose,
      ...toastProps
    }: SnackbarOptions) => {
      setToastProps(toastProps);

      const handleClose = () => {
        onClose?.();
        setSnackbarProps((prev) => ({ ...prev, open: false }));
      };

      setSnackbarProps({
        open: true,
        autoHideDuration: duration,
        onClose: handleClose,
        anchorOrigin: position,
      });

      setIconProps({ severity, onClose: handleClose });
    },
    [],
  );

  const showError = useCallback(
    (options: Omit<SnackbarOptions, 'severity'>) => {
      show({ ...options, severity: 'error' });
    },
    [show],
  );

  const showSuccess = useCallback(
    (options: Omit<SnackbarOptions, 'severity'>) => {
      show({ ...options, severity: 'success' });
    },
    [show],
  );

  const showErrorMessage = useCallback(
    (e: unknown, options?: Omit<SnackbarOptions, 'severity' | 'message'>) => {
      let message = t('에러가 발생했습니다.');
      if (e instanceof AxiosError && e.response?.data?.message) {
        message = e.response.data.message;
      }
      show({ ...options, message, severity: 'error' });
    },
    [show],
  );

  return (
    <SnackbarContext.Provider
      value={{ show, showError, showErrorMessage, showSuccess }}
    >
      {children}
      <Snackbar
        {...toastProps}
        iconProps={iconProps}
        snackbarProps={snackbarProps}
      />
    </SnackbarContext.Provider>
  );
};

export { SnackbarProvider, SnackbarContext };
